import AsyncStorage from "@react-native-async-storage/async-storage";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import {
  MOCK_CARD_MEMOS,
  MOCK_POSTS,
  MOCK_USER,
} from "../constants/mockData";
import type { ThemeMode } from "../constants/theme";
import { getEmailConfirmRedirectUrl } from "../lib/authRedirect";
import { uploadMediaAsync, uploadMediaListAsync } from "../lib/mediaUpload";
import { isSupabaseConfigured, supabase } from "../lib/supabase";
import {
  ClassGroup,
  ClassMembership,
  CardMemo,
  CardUsageRecord,
  Comment,
  Card,
  LibraryVisibilityRule,
  Post,
  Profile,
  Reaction,
  ReactionType,
  Report,
  School,
  Subject,
  TeacherAssignment,
  TeacherTab,
} from "../types";

type ReactionSummary = Record<ReactionType, number>;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const REACTION_TYPES: ReactionType[] = [
  "empathy",
  "surprise",
  "deep",
  "useful",
  "connected",
];

const createEmptyReactionSummary = (): ReactionSummary => ({
  empathy: 0,
  surprise: 0,
  deep: 0,
  useful: 0,
  connected: 0,
});

const createClientId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const isLibraryManager = (profile?: Profile | null) =>
  profile?.role === "admin";

const createMutationError = (fallbackMessage: string, error?: { message?: string } | null) =>
  new Error(error?.message ?? fallbackMessage);

const getAuthErrorMessage = (fallbackMessage: string, error?: { code?: string; message?: string } | null) => {
  if (!error) return fallbackMessage;

  if (error.code === "over_email_send_rate_limit") {
    return "確認メールの送信上限に達しています。少し時間をおいて再度お試しください。公開前はSupabaseのAuth > SMTPで独自SMTPを設定してください。";
  }

  if (
    error.code === "unexpected_failure" &&
    error.message?.toLowerCase().includes("confirmation email")
  ) {
    return "確認メールを送信できませんでした。SupabaseのAuthentication > Emails > SMTP Settingsで独自SMTPを設定するか、開発中はConfirm emailをOFFにしてください。";
  }

  if (error.message?.toLowerCase().includes("email address not authorized")) {
    return "このメールアドレスには確認メールを送信できません。Supabase標準メールでは送信先が制限されるため、独自SMTPを設定してください。";
  }

  if (error.code === "email_exists" || error.message?.toLowerCase().includes("already registered")) {
    return "このメールアドレスはすでに登録されています。ログインをお試しください。";
  }

  if (error.message?.toLowerCase().includes("password")) {
    return "パスワード条件を満たしていません。8文字以上で、英数字を含めてください。";
  }

  if (error.message?.toLowerCase().includes("invalid email")) {
    return "メールアドレスの形式を確認してください。";
  }

  return `${fallbackMessage}（${error.message}）`;
};

const mergeProfiles = (profiles: Profile[], incomingProfiles: Profile[]) => {
  const profilesById = new Map(profiles.map((profile) => [profile.id, profile]));

  incomingProfiles.forEach((profile) => {
    profilesById.set(profile.id, profile);
  });

  return Array.from(profilesById.values());
};

const buildProfileFromAuthUser = (
  user: {
    id: string;
    email?: string;
    user_metadata?: { display_name?: string; avatar_url?: string };
  },
  displayName?: string,
): Profile => ({
  id: user.id,
  display_name:
    displayName?.trim() ||
    user.user_metadata?.display_name ||
    user.email?.split("@")[0] ||
    "学問ユーザー",
  affiliation: "未設定",
  role: "student",
  avatar_url: user.user_metadata?.avatar_url,
});

const fetchProfile = async (userId: string) => {
  const { data, error } = await supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .maybeSingle();

  if (error) {
    console.warn("Failed to fetch profile from Supabase", error);
    return null;
  }

  return data as Profile | null;
};

const saveProfile = async (profile: Profile) => {
  const profilePayload = {
    id: profile.id,
    display_name: profile.display_name,
    grade: profile.grade,
    affiliation: profile.affiliation,
    bio: profile.bio,
    avatar_url: profile.avatar_url,
  };

  const { data, error } = await supabase
    .from("profiles")
    .upsert(profilePayload)
    .select()
    .single();

  if (error) {
    throw createMutationError("Failed to save profile to Supabase", error);
  }

  return data as Profile;
};

const loadProfilesFromSupabase = async (userIds: string[]) => {
  const uniqueUserIds = Array.from(new Set(userIds)).filter((id) =>
    UUID_PATTERN.test(id),
  );

  if (uniqueUserIds.length === 0) return [];

  const { data, error } = await supabase
    .from("profiles")
    .select("id, display_name, grade, affiliation, bio, role, avatar_url")
    .in("id", uniqueUserIds);

  if (error) {
    console.warn("Failed to load profiles from Supabase", error);
    return [];
  }

  return data as Profile[];
};

const loadPostsFromSupabase = async () => {
  const { data, error } = await supabase
    .from("posts")
    .select("*")
    .neq("status", "deleted")
    .order("created_at", { ascending: false });

  if (error) {
    console.warn("Failed to load posts from Supabase", error);
    return null;
  }

  return data as Post[];
};

const loadTable = async <T>(tableName: string) => {
  const { data, error } = await supabase.from(tableName).select("*");

  if (error) {
    console.warn(`Failed to load ${tableName} from Supabase`, error);
    return null;
  }

  return data as T[];
};

const loadSubjectsFromSupabase = async () => {
  const { data, error } = await supabase
    .from("subjects")
    .select("*")
    .eq("is_active", true)
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });

  if (error) {
    console.warn("Failed to load subjects from Supabase", error);
    return null;
  }

  return data as Subject[];
};

const loadCardsFromSupabase = async () => {
  const { data, error } = await supabase
    .from("cards")
    .select("*")
    .eq("is_active", true)
    .order("sort_order", { ascending: true })
    .order("title", { ascending: true });

  if (error) {
    console.warn("Failed to load cards from Supabase", error);
    return null;
  }

  return data as Card[];
};

interface AppState {
  currentUser: Profile | null;
  authLoading: boolean;
  authError: string | null;
  authMessage: string | null;
  posts: Post[];
  profiles: Profile[];
  subjects: Subject[];
  cards: Card[];
  cardMemos: CardMemo[];
  cardUsageRecords: CardUsageRecord[];
  reactions: Reaction[];
  comments: Comment[];
  reports: Report[];
  schools: School[];
  classes: ClassGroup[];
  classMemberships: ClassMembership[];
  teacherAssignments: TeacherAssignment[];
  teacherTabs: TeacherTab[];
  libraryVisibilityRules: LibraryVisibilityRule[];
  hiddenPostIds: string[];
  hasSeenOnboarding: boolean;
  hasHydrated: boolean;
  themeMode: ThemeMode;
  completeOnboarding: () => void;
  setHasHydrated: (value: boolean) => void;
  setThemeMode: (mode: ThemeMode) => void;
  addPost: (post: Omit<Post, "id" | "user_id" | "created_at">) => Promise<void>;
  addCardMemo: (
    memo: Omit<CardMemo, "id" | "created_at" | "updated_at">,
  ) => Promise<void>;
  updateProfile: (profileData: Partial<Profile>) => Promise<void>;
  loadCardMemos: () => Promise<void>;
  loadCurrentUser: () => Promise<void>;
  loadRemoteData: () => Promise<void>;
  loadLibraryContent: () => Promise<void>;
  loadTeachingContext: () => Promise<void>;
  upsertTeacherTab: (tab: TeacherTab) => Promise<void>;
  upsertSubject: (subject: Subject) => Promise<void>;
  upsertCard: (card: Card) => Promise<void>;
  archiveCard: (cardId: string) => Promise<void>;
  recordCardUsage: (cardId: string, subjectId: string) => Promise<void>;
  getCardUsageStats: () => {
    card_id: string;
    count: number;
    subject_id: string;
  }[];
  login: (email?: string, password?: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<void>;
  logout: () => Promise<void>;
  deleteAccount: () => Promise<void>;
  addReaction: (postId: string, reactionType: ReactionType) => Promise<void>;
  getPostReactionCounts: (postId: string) => ReactionSummary;
  addComment: (postId: string, body: string, preset?: boolean) => Promise<void>;
  getCommentsForPost: (postId: string) => Comment[];
  getProfileById: (userId: string) => Profile | null;
  reportPost: (postId: string, reason: string) => Promise<void>;
  hidePost: (postId: string) => void;
  deletePost: (postId: string) => Promise<void>;
}

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      currentUser: null,
      authLoading: false,
      authError: null,
      authMessage: null,
      posts: isSupabaseConfigured ? [] : [...MOCK_POSTS],
      profiles: isSupabaseConfigured ? [] : [MOCK_USER],
      subjects: [],
      cards: [],
      cardMemos: isSupabaseConfigured ? [] : [...MOCK_CARD_MEMOS],
      cardUsageRecords: [],
      reactions: [],
      comments: [],
      reports: [],
      schools: [],
      classes: [],
      classMemberships: [],
      teacherAssignments: [],
      teacherTabs: [],
      libraryVisibilityRules: [],
      hiddenPostIds: [],
      hasSeenOnboarding: false,
      hasHydrated: false,
      themeMode: "system",

      completeOnboarding: () => set({ hasSeenOnboarding: true }),
      setHasHydrated: (value) => set({ hasHydrated: value }),
      setThemeMode: (mode) => set({ themeMode: mode }),

      addPost: async (postData) => {
        const state = get();
        if (!state.currentUser) {
          throw new Error("ログインが必要です。");
        }

        const newPost: Post = {
          ...postData,
          id: createClientId("post"),
          user_id: state.currentUser.id,
          created_at: new Date().toISOString(),
          status: "visible",
        };

        const postToSave: Post = {
          ...newPost,
          media_urls: await uploadMediaListAsync(
            newPost.media_urls,
            `${state.currentUser.id}/posts`,
          ),
        };

        if (isSupabaseConfigured) {
          const { data, error } = await supabase
            .from("posts")
            .insert(postToSave)
            .select()
            .single();

          if (error || !data) {
            throw createMutationError("Failed to save post to Supabase", error);
          }

          set((currentState) => ({
            posts: [data as Post, ...currentState.posts],
            profiles: mergeProfiles(currentState.profiles, [state.currentUser!]),
          }));
          return;
        }

        set((currentState) => ({
          posts: [postToSave, ...currentState.posts],
          profiles: mergeProfiles(currentState.profiles, [state.currentUser!]),
        }));
      },

      addCardMemo: async (memoData) => {
        const state = get();
        if (!state.currentUser) {
          throw new Error("ログインが必要です。");
        }

        const newMemo: CardMemo = {
          ...memoData,
          id: createClientId("memo"),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        if (isSupabaseConfigured) {
          const { data, error } = await supabase
            .from("card_memos")
            .insert(newMemo)
            .select()
            .single();

          if (error || !data) {
            throw createMutationError("Failed to save memo to Supabase", error);
          }

          set((currentState) => ({
            cardMemos: [data as CardMemo, ...currentState.cardMemos],
          }));
          return;
        }

        set((currentState) => ({
          cardMemos: [newMemo, ...currentState.cardMemos],
        }));
      },

      updateProfile: async (profileData) => {
        const state = get();
        if (!state.currentUser) return;

        const hasAvatarUpdate = Object.prototype.hasOwnProperty.call(
          profileData,
          "avatar_url",
        );
        const avatarUrl =
          hasAvatarUpdate && profileData.avatar_url
            ? await uploadMediaAsync(
                profileData.avatar_url,
                `${state.currentUser.id}/profiles`,
              )
            : hasAvatarUpdate
              ? undefined
              : state.currentUser.avatar_url;

        const updatedProfile: Profile = {
          ...state.currentUser,
          ...profileData,
          role: state.currentUser.role,
          avatar_url: avatarUrl,
        };

        if (isSupabaseConfigured) {
          const savedProfile = await saveProfile(updatedProfile);
          set((currentState) => ({
            currentUser: savedProfile,
            profiles: mergeProfiles(currentState.profiles, [savedProfile]),
          }));
          return;
        }

        set((currentState) => ({
          currentUser: updatedProfile,
          profiles: mergeProfiles(currentState.profiles, [updatedProfile]),
        }));
      },

      loadCardMemos: async () => {
        const state = get();
        if (!state.currentUser || !isSupabaseConfigured) return;

        const { data, error } = await supabase
          .from("card_memos")
          .select("*")
          .eq("user_id", state.currentUser.id);

        if (!error && data) {
          set({ cardMemos: data as CardMemo[] });
          return;
        }

        console.warn("Failed to load card memos from Supabase", error);
      },

      loadCurrentUser: async () => {
        if (!isSupabaseConfigured) return;

        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        const user = session?.user;

        if (error || !user) {
          set({ currentUser: null });
          await get().loadRemoteData();
          return;
        }

        const fallbackProfile = buildProfileFromAuthUser(user);
        let profile = await fetchProfile(user.id);

        if (!profile) {
          try {
            profile = await saveProfile(fallbackProfile);
          } catch (profileError) {
            console.warn(
              "Failed to save profile while restoring session",
              profileError,
            );
            profile = fallbackProfile;
          }
        }

        set((currentState) => ({
          currentUser: profile,
          profiles: mergeProfiles(currentState.profiles, [profile]),
        }));
        await get().loadRemoteData();
      },

      loadRemoteData: async () => {
        if (!isSupabaseConfigured) return;

        const currentUserId = get().currentUser?.id;
        const [
          loadedSubjects,
          loadedCards,
          loadedPosts,
          loadedReactions,
          loadedComments,
          loadedReports,
          loadedCardUsageRecords,
        ] = await Promise.all([
          loadSubjectsFromSupabase(),
          loadCardsFromSupabase(),
          loadPostsFromSupabase(),
          loadTable<Reaction>("reactions"),
          loadTable<Comment>("comments"),
          currentUserId ? loadTable<Report>("reports") : Promise.resolve(null),
          currentUserId
            ? loadTable<CardUsageRecord>("card_usage_records")
            : Promise.resolve(null),
        ]);

        const profileUserIds = [
          ...(get().currentUser ? [get().currentUser!.id] : []),
          ...((loadedPosts ?? []) as Post[]).map((post) => post.user_id),
          ...((loadedComments ?? []) as Comment[]).map(
            (comment) => comment.user_id,
          ),
          ...((loadedReactions ?? []) as Reaction[]).map(
            (reaction) => reaction.user_id,
          ),
        ];
        const loadedProfiles = await loadProfilesFromSupabase(profileUserIds);

        set((currentState) => ({
          ...(loadedSubjects ? { subjects: loadedSubjects } : {}),
          ...(loadedCards ? { cards: loadedCards } : {}),
          ...(loadedPosts ? { posts: loadedPosts } : {}),
          ...(loadedReactions ? { reactions: loadedReactions } : {}),
          ...(loadedComments ? { comments: loadedComments } : {}),
          ...(loadedReports ? { reports: loadedReports } : {}),
          ...(loadedCardUsageRecords
            ? { cardUsageRecords: loadedCardUsageRecords }
            : {}),
          profiles: mergeProfiles(
            currentState.profiles,
            [
              ...(currentState.currentUser ? [currentState.currentUser] : []),
              ...loadedProfiles,
            ],
          ),
        }));
      },

      loadLibraryContent: async () => {
        if (!isSupabaseConfigured) return;

        const [loadedSubjects, loadedCards] = await Promise.all([
          loadSubjectsFromSupabase(),
          loadCardsFromSupabase(),
        ]);

        set({
          ...(loadedSubjects ? { subjects: loadedSubjects } : {}),
          ...(loadedCards ? { cards: loadedCards } : {}),
        });
      },

      loadTeachingContext: async () => {
        const state = get();
        if (!isSupabaseConfigured || !state.currentUser) return;

        if (
          state.currentUser.role !== "teacher" &&
          state.currentUser.role !== "admin"
        ) {
          set({
            schools: [],
            classes: [],
            classMemberships: [],
            teacherAssignments: [],
            teacherTabs: [],
            libraryVisibilityRules: [],
          });
          return;
        }

        const [
          schoolsResult,
          classesResult,
          membershipsResult,
          assignmentsResult,
          tabsResult,
          visibilityResult,
        ] = await Promise.all([
          supabase.from("schools").select("*").order("name"),
          supabase.from("classes").select("*").order("sort_order"),
          supabase.from("class_memberships").select("*"),
          supabase.from("teacher_assignments").select("*"),
          supabase.from("teacher_tabs").select("*").order("sort_order"),
          supabase.from("library_visibility_rules").select("*"),
        ]);

        for (const result of [
          schoolsResult,
          classesResult,
          membershipsResult,
          assignmentsResult,
          tabsResult,
          visibilityResult,
        ]) {
          if (result.error) {
            console.warn("Failed to load teaching context", result.error);
          }
        }

        set({
          schools: (schoolsResult.data ?? []) as School[],
          classes: (classesResult.data ?? []) as ClassGroup[],
          classMemberships: (membershipsResult.data ?? []) as ClassMembership[],
          teacherAssignments: (assignmentsResult.data ?? []) as TeacherAssignment[],
          teacherTabs: (tabsResult.data ?? []) as TeacherTab[],
          libraryVisibilityRules: (visibilityResult.data ?? []) as LibraryVisibilityRule[],
        });
      },

      upsertTeacherTab: async (tab) => {
        const state = get();
        if (!state.currentUser) {
          throw new Error("ログインが必要です。");
        }

        if (
          state.currentUser.role !== "teacher" &&
          state.currentUser.role !== "admin"
        ) {
          throw new Error("先生用タブを管理する権限がありません。");
        }

        const tabToSave: TeacherTab = {
          ...tab,
          teacher_id: tab.teacher_id || state.currentUser.id,
          is_active: tab.is_active ?? true,
          updated_at: new Date().toISOString(),
        };

        if (isSupabaseConfigured) {
          const { data, error } = await supabase
            .from("teacher_tabs")
            .upsert(tabToSave)
            .select()
            .single();

          if (error || !data) {
            throw createMutationError("Failed to save teacher tab", error);
          }

          set((currentState) => ({
            teacherTabs: [
              ...currentState.teacherTabs.filter((item) => item.id !== tabToSave.id),
              data as TeacherTab,
            ].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
          }));
          return;
        }

        set((currentState) => ({
          teacherTabs: [
            ...currentState.teacherTabs.filter((item) => item.id !== tabToSave.id),
            tabToSave,
          ],
        }));
      },

      upsertSubject: async (subject) => {
        const state = get();
        if (!isLibraryManager(state.currentUser)) {
          throw new Error("学問レンズ図書館を編集する権限がありません。");
        }

        const subjectToSave = {
          ...subject,
          is_active: subject.is_active ?? true,
          sort_order: subject.sort_order ?? state.subjects.length + 1,
        };

        if (isSupabaseConfigured) {
          const { data, error } = await supabase
            .from("subjects")
            .upsert(subjectToSave)
            .select()
            .single();

          if (error || !data) {
            throw createMutationError("Failed to save subject to Supabase", error);
          }

          set((currentState) => ({
            subjects: [
              ...currentState.subjects.filter((item) => item.id !== subject.id),
              data as Subject,
            ].sort(
              (a, b) =>
                (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
                a.name.localeCompare(b.name),
            ),
          }));
          return;
        }

        set((currentState) => ({
          subjects: [
            ...currentState.subjects.filter((item) => item.id !== subject.id),
            subjectToSave,
          ],
        }));
      },

      upsertCard: async (card) => {
        const state = get();
        if (!isLibraryManager(state.currentUser)) {
          throw new Error("学問レンズ図書館を編集する権限がありません。");
        }

        const now = new Date().toISOString();
        const cardToSave = {
          ...card,
          thinking_tags: card.thinking_tags.filter(Boolean),
          career_tags: card.career_tags?.filter(Boolean) ?? [],
          related_card_ids: card.related_card_ids?.filter(Boolean) ?? [],
          video_urls: card.video_urls?.filter(Boolean) ?? [],
          sort_order: card.sort_order ?? state.cards.length + 1,
          is_active: card.is_active ?? true,
          updated_at: now,
        };

        if (isSupabaseConfigured) {
          const { data, error } = await supabase
            .from("cards")
            .upsert(cardToSave)
            .select()
            .single();

          if (error || !data) {
            throw createMutationError("Failed to save card to Supabase", error);
          }

          set((currentState) => ({
            cards: [
              ...currentState.cards.filter((item) => item.id !== card.id),
              data as Card,
            ].sort(
              (a, b) =>
                (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
                a.title.localeCompare(b.title),
            ),
          }));
          return;
        }

        set((currentState) => ({
          cards: [
            ...currentState.cards.filter((item) => item.id !== card.id),
            cardToSave,
          ],
        }));
      },

      archiveCard: async (cardId) => {
        const state = get();
        if (!isLibraryManager(state.currentUser)) {
          throw new Error("学問レンズ図書館を編集する権限がありません。");
        }

        if (isSupabaseConfigured) {
          const { error } = await supabase
            .from("cards")
            .update({
              is_active: false,
              updated_at: new Date().toISOString(),
            })
            .eq("id", cardId);

          if (error) {
            throw createMutationError("Failed to archive card in Supabase", error);
          }
        }

        set((currentState) => ({
          cards: currentState.cards.filter((card) => card.id !== cardId),
        }));
      },

      recordCardUsage: async (cardId, subjectId) => {
        const state = get();
        if (!state.currentUser) return;

        const existingRecord = state.cardUsageRecords.find(
          (r) => r.card_id === cardId && r.user_id === state.currentUser?.id,
        );

        if (existingRecord) {
          const updatedRecord = {
            ...existingRecord,
            usage_count: existingRecord.usage_count + 1,
            last_used_at: new Date().toISOString(),
          };

          set((currentState) => ({
            cardUsageRecords: currentState.cardUsageRecords.map((r) =>
              r.card_id === cardId && r.user_id === state.currentUser?.id
                ? updatedRecord
                : r,
            ),
          }));

          if (isSupabaseConfigured) {
            const { error } = await supabase
              .from("card_usage_records")
              .upsert(updatedRecord, { onConflict: "user_id,card_id" });
            if (error) {
              console.warn("Failed to save card usage to Supabase", error);
            }
          }
          return;
        }

        const newRecord: CardUsageRecord = {
          card_id: cardId,
          user_id: state.currentUser.id,
          subject_id: subjectId,
          usage_count: 1,
          last_used_at: new Date().toISOString(),
        };

        set((currentState) => ({
          cardUsageRecords: [...currentState.cardUsageRecords, newRecord],
        }));

        if (isSupabaseConfigured) {
          const { error } = await supabase
            .from("card_usage_records")
            .upsert(newRecord, { onConflict: "user_id,card_id" });
          if (error) {
            console.warn("Failed to save card usage to Supabase", error);
          }
        }
      },

      getCardUsageStats: () => {
        const state = get();
        const currentUserId = state.currentUser?.id;
        if (!currentUserId) return [];

        const stats = state.cardUsageRecords
          .filter((r) => r.user_id === currentUserId)
          .reduce(
            (acc, r) => {
              const existing = acc.find((a) => a.card_id === r.card_id);
              if (existing) {
                existing.count += r.usage_count;
              } else {
                acc.push({
                  card_id: r.card_id,
                  count: r.usage_count,
                  subject_id: r.subject_id,
                });
              }
              return acc;
            },
            [] as { card_id: string; count: number; subject_id: string }[],
          );

        return stats.sort((a, b) => b.count - a.count);
      },

      login: async (email, password) => {
        set({ authLoading: true, authError: null, authMessage: null });

        try {
          if (!isSupabaseConfigured) {
            set((currentState) => ({
              currentUser: MOCK_USER,
              authLoading: false,
              authMessage: null,
              profiles: mergeProfiles(currentState.profiles, [MOCK_USER]),
            }));
            return;
          }

          if (!email || !password) {
            set({
              authLoading: false,
              authError: "メールアドレスとパスワードを入力してください。",
              authMessage: null,
            });
            return;
          }

          const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (error || !data.user) {
            set({
              authLoading: false,
              authError: getAuthErrorMessage(
                "ログインに失敗しました。メールとパスワードを確認してください。",
                error,
              ),
              authMessage: null,
            });
            return;
          }

          const profile =
            (await fetchProfile(data.user.id)) ??
            (await saveProfile(buildProfileFromAuthUser(data.user)));

          set((currentState) => ({
            currentUser: profile,
            authLoading: false,
            authError: null,
            authMessage: null,
            profiles: mergeProfiles(currentState.profiles, [profile]),
          }));
          await get().loadRemoteData();
        } catch (error) {
          console.warn("Failed to login", error);
          set({
            authLoading: false,
            authError: "ログインに失敗しました。通信状態を確認してください。",
            authMessage: null,
          });
        }
      },

      signUp: async (email, password, displayName) => {
        set({ authLoading: true, authError: null, authMessage: null });

        try {
          if (!isSupabaseConfigured) {
            if (!__DEV__) {
              set({
                authLoading: false,
                authError:
                  "認証サーバーに接続できません。アプリの設定を確認してください。",
                authMessage: null,
              });
              return;
            }

            const localProfile = {
              ...MOCK_USER,
              id: createClientId("local-user"),
              display_name:
                displayName?.trim() || email.split("@")[0] || "学問ユーザー",
            };
            set({
              currentUser: localProfile,
              authLoading: false,
              authMessage: null,
              profiles: [localProfile],
            });
            return;
          }

          const { data, error } = await supabase.auth.signUp({
            email,
            password,
            options: {
              emailRedirectTo: getEmailConfirmRedirectUrl(),
              data: {
                display_name: displayName,
              },
            },
          });

          if (error || !data.user) {
            set({
              authLoading: false,
              authError: getAuthErrorMessage(
                "アカウント作成に失敗しました。入力内容を確認してください。",
                error,
              ),
              authMessage: null,
            });
            return;
          }

          if (!data.session) {
            set({
              currentUser: null,
              authLoading: false,
              authError: null,
              authMessage:
                "確認メールを送信しました。メール内のリンクで確認後、この画面からログインしてください。",
            });
            return;
          }

          const profile =
            (await fetchProfile(data.user.id)) ??
            (await saveProfile(buildProfileFromAuthUser(data.user, displayName)));

          set((currentState) => ({
            currentUser: profile,
            authLoading: false,
            authError: null,
            authMessage: null,
            profiles: mergeProfiles(currentState.profiles, [profile]),
          }));
          await get().loadRemoteData();
        } catch (error) {
          console.warn("Failed to sign up", error);
          set({
            authLoading: false,
            authError: "アカウント作成に失敗しました。通信状態を確認してください。",
            authMessage: null,
          });
        }
      },

      logout: async () => {
        if (isSupabaseConfigured) {
          await supabase.auth.signOut();
        }

        set({
          currentUser: null,
          authError: null,
          authMessage: null,
          cardMemos: [],
        });
        await get().loadRemoteData();
      },

      deleteAccount: async () => {
        const state = get();

        if (!state.currentUser) {
          throw new Error("ログインが必要です。");
        }

        set({ authLoading: true, authError: null, authMessage: null });

        try {
          if (isSupabaseConfigured) {
            const {
              data: { session },
              error: sessionError,
            } = await supabase.auth.getSession();

            if (sessionError || !session?.access_token) {
              throw new Error("安全確認のため、もう一度ログインしてください。");
            }

            const { error } = await supabase.functions.invoke("delete-account", {
              method: "POST",
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              },
            });

            if (error) {
              throw createMutationError("Failed to delete account", error);
            }

            await supabase.auth.signOut();
          }

          set({
            currentUser: null,
            authLoading: false,
            authError: null,
            authMessage: null,
            cardMemos: [],
            cardUsageRecords: [],
            reports: [],
          });
          await get().loadRemoteData();
        } catch (error) {
          set({ authLoading: false });
          throw error;
        }
      },

      addReaction: async (postId, reactionType) => {
        const state = get();
        if (!state.currentUser) {
          throw new Error("ログインが必要です。");
        }

        const existingReactions = state.reactions.filter(
          (reaction) =>
            reaction.post_id === postId &&
            reaction.user_id === state.currentUser?.id,
        );
        const existingReaction = existingReactions.find(
          (reaction) => reaction.reaction_type === reactionType,
        );

        if (isSupabaseConfigured) {
          const { error: deleteError } = await supabase
            .from("reactions")
            .delete()
            .eq("post_id", postId)
            .eq("user_id", state.currentUser.id);

          if (deleteError) {
            throw createMutationError(
              "Failed to delete reaction from Supabase",
              deleteError,
            );
          }
        }

        if (existingReaction) {
          set((currentState) => ({
            reactions: currentState.reactions.filter(
              (reaction) =>
                !(
                  reaction.post_id === postId &&
                  reaction.user_id === state.currentUser?.id
                ),
            ),
          }));
          return;
        }

        const newReaction: Reaction = {
          id: createClientId(`reaction-${reactionType}`),
          post_id: postId,
          user_id: state.currentUser.id,
          reaction_type: reactionType,
          created_at: new Date().toISOString(),
        };

        if (isSupabaseConfigured) {
          const { data, error } = await supabase
            .from("reactions")
            .insert(newReaction)
            .select()
            .single();

          if (error || !data) {
            throw createMutationError("Failed to save reaction to Supabase", error);
          }

          set((currentState) => ({
            reactions: [
              ...currentState.reactions.filter(
                (reaction) =>
                  !(
                    reaction.post_id === postId &&
                    reaction.user_id === state.currentUser?.id
                  ),
              ),
              data as Reaction,
            ],
          }));
          return;
        }

        set((currentState) => ({
          reactions: [
            ...currentState.reactions.filter(
              (reaction) =>
                !(
                  reaction.post_id === postId &&
                  reaction.user_id === state.currentUser?.id
                ),
            ),
            newReaction,
          ],
        }));
      },

      getPostReactionCounts: (postId) => {
        const summary = createEmptyReactionSummary();

        get()
          .reactions.filter((reaction) => reaction.post_id === postId)
          .forEach((reaction) => {
            if (REACTION_TYPES.includes(reaction.reaction_type)) {
              summary[reaction.reaction_type] += 1;
            }
          });

        return summary;
      },

      addComment: async (postId, body, preset = false) => {
        const state = get();
        if (!state.currentUser) {
          throw new Error("ログインが必要です。");
        }

        const normalizedBody = body.trim().slice(0, 240);
        if (!normalizedBody) return;

        const newComment: Comment = {
          id: createClientId("comment"),
          post_id: postId,
          user_id: state.currentUser.id,
          body: normalizedBody,
          preset,
          status: "visible",
          created_at: new Date().toISOString(),
        };

        if (isSupabaseConfigured) {
          const { data, error } = await supabase
            .from("comments")
            .insert(newComment)
            .select()
            .single();

          if (error || !data) {
            throw createMutationError("Failed to save comment to Supabase", error);
          }

          set((currentState) => ({
            comments: [...currentState.comments, data as Comment],
            profiles: mergeProfiles(currentState.profiles, [state.currentUser!]),
          }));
          return;
        }

        set((currentState) => ({
          comments: [...currentState.comments, newComment],
          profiles: mergeProfiles(currentState.profiles, [state.currentUser!]),
        }));
      },

      getCommentsForPost: (postId) =>
        get()
          .comments.filter(
            (comment) => comment.post_id === postId && comment.status === "visible",
          )
          .sort((a, b) => a.created_at.localeCompare(b.created_at)),

      getProfileById: (userId) => {
        const state = get();
        if (state.currentUser?.id === userId) return state.currentUser;
        return (
          state.profiles.find((profile) => profile.id === userId) ??
          (userId === MOCK_USER.id ? MOCK_USER : null)
        );
      },

      reportPost: async (postId, reason) => {
        const state = get();
        if (!state.currentUser) {
          throw new Error("ログインが必要です。");
        }

        const existingReport = state.reports.find(
          (report) =>
            report.post_id === postId &&
            report.user_id === state.currentUser?.id &&
            report.status === "open",
        );

        if (existingReport) {
          get().hidePost(postId);
          return;
        }

        const newReport: Report = {
          id: createClientId("report"),
          post_id: postId,
          user_id: state.currentUser.id,
          reason,
          status: "open",
          created_at: new Date().toISOString(),
        };

        if (isSupabaseConfigured) {
          const { data, error } = await supabase
            .from("reports")
            .insert(newReport)
            .select()
            .single();

          if (error || !data) {
            throw createMutationError("Failed to save report to Supabase", error);
          }

          set((currentState) => ({
            reports: [...currentState.reports, data as Report],
            hiddenPostIds: currentState.hiddenPostIds.includes(postId)
              ? currentState.hiddenPostIds
              : [...currentState.hiddenPostIds, postId],
          }));
          return;
        }

        set((currentState) => ({
          reports: [...currentState.reports, newReport],
          hiddenPostIds: currentState.hiddenPostIds.includes(postId)
            ? currentState.hiddenPostIds
            : [...currentState.hiddenPostIds, postId],
        }));
      },

      hidePost: (postId) =>
        set((state) => ({
          hiddenPostIds: state.hiddenPostIds.includes(postId)
            ? state.hiddenPostIds
            : [...state.hiddenPostIds, postId],
        })),

      deletePost: async (postId) => {
        const state = get();
        const post = state.posts.find((item) => item.id === postId);
        const canDelete =
          post?.user_id === state.currentUser?.id ||
          state.currentUser?.role === "teacher" ||
          state.currentUser?.role === "admin";

        if (!canDelete) return;

        const updatedAt = new Date().toISOString();

        if (isSupabaseConfigured) {
          const { error } = await supabase
            .from("posts")
            .update({ status: "deleted", updated_at: updatedAt })
            .eq("id", postId);
          if (error) {
            throw createMutationError("Failed to delete post from Supabase", error);
          }
        }

        set((currentState) => ({
          posts: currentState.posts.map((item) =>
            item.id === postId
              ? {
                  ...item,
                  status: "deleted",
                  updated_at: updatedAt,
                }
              : item,
          ),
        }));
      },
    }),
    {
      name: "gakumonlens-app-state-v2",
      version: 3,
      storage: createJSONStorage(() => AsyncStorage),
      migrate: (persistedState) => {
        const state = persistedState as Partial<AppState> | undefined;

        return {
          ...state,
          subjects: [],
          cards: [],
          cardUsageRecords: [],
        };
      },
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
      partialize: (state) =>
        isSupabaseConfigured
          ? {
              hiddenPostIds: state.hiddenPostIds,
              hasSeenOnboarding: state.hasSeenOnboarding,
              themeMode: state.themeMode,
            }
          : {
              currentUser: state.currentUser,
              posts: state.posts,
              profiles: state.profiles,
              cardMemos: state.cardMemos,
              reactions: state.reactions,
              comments: state.comments,
              reports: state.reports,
              hiddenPostIds: state.hiddenPostIds,
              hasSeenOnboarding: state.hasSeenOnboarding,
              themeMode: state.themeMode,
            },
    },
  ),
);
